import React, { useEffect, useState, useRef } from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import api from "../Utils/api";

import ModifyDialog from "../Components/Location/ModifyDialog";
import DeleteDialog from "../Components/Location/DeleteDialog";
import ShowMapDialog from "../Components/Location/ShowMap";
import SettingButton from "../Components/Location/SettingButton";
import IrelandMap from "../Components/Location/IrelandMap";
import Spinner from "../Components/Spinner";

export default function LocationsPage() {
  const [locations, setLocations] = useState([]);
  const [locationData, setLocationData] = useState({});
  const [displayMode, setDisplayMode] = useState("grid");
  const [hiddenLocationIds, setHiddenLocationIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [modifyDialogLocation, setModifyDialogLocation] = useState(null);
  const [deleteDialogLocation, setDeleteDialogLocation] = useState(null);
  const [showMapDialogLocation, setShowMapDialogLocation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [pendingLocationID, setPendingLocationID] = useState(null);

  const navigate = useNavigate();

  const user = useSelector((state) => state.user).user;

  useEffect(() => {
    getLocations();
  }, []);

  const changeDisplayMode = (mode) => {
    setDisplayMode(mode);
  };

  const getLocations = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/location");
      setIsLoading(false);
      setLocations(response.data.locations);
      setFilteredLocations(response.data.locations);
    } catch (error) {
      console.error(error);
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Filter locations by search term (name, address, area)
  // const filteredLocations = locations.filter((loc) => {
  //   if (hiddenLocationIds.has(loc._id)) return false;

  //   const search = searchTerm.toLowerCase();
  //   return (
  //     loc.name.toLowerCase().includes(search) ||
  //     loc.address.toLowerCase().includes(search) ||
  //     (loc.area && loc.area.toLowerCase().includes(search))
  //   );
  // });

  const [filteredLocations, setFilteredLocations] = useState(locations);

  const handleLocationSearch = () => {
    const filteredLocationsData = locations.filter((location) => {
      const search = searchTerm.toLowerCase();
      return (
        location.name.toLowerCase().includes(search) ||
        (location.address && location.address.toLowerCase().includes(search))
      );
    });
    setFilteredLocations(filteredLocationsData);
  };

  const handleLocationKeyDown = (e) => {
    if (e.key === "Enter") {
      handleLocationSearch();
    }
  };

  // Hide location locally
  const handleHideLocation = async (locationId) => {
    // const res = await api.post("/schedule/hide", {
    //   id: locationId,
    // });
    // if (res.data.success) {
    //   toast.success(res.data.message);
    //   getLocations();
    // }
  };

  // Save modified location locally (simulate API save)
  const handleSaveModifiedLocation = (updatedLocation) => {
    setLocations((prev) =>
      prev.map((loc) =>
        loc._id === updatedLocation._id ? updatedLocation : loc
      )
    );
    setModifyDialogLocation(null);
  };

  const opemModifyLocationDialog = (location) => {
    setModifyDialogLocation(location);
  };

  const updateLocation = async (updatedLocation) => {
    const res = await api.post("/location/update", {
      id: modifyDialogLocation._id,
      updatedLocation,
    });

    if (res.data.success) {
      toast.success(res.data.message);
      getLocations();
      setModifyDialogLocation(null);
    } else {
      toast.error(res.data.message);
    }
  };

  const openDeleteLocationDialog = (location) => {
    setDeleteDialogLocation(location);
  };

  const [isActiveChecked, setIsActiveChecked] = useState(true);
  const [isArchivedChecked, setIsArchivedChecked] = useState(true);

  const filterLocations = (activeChecked, archivedChecked) => {
    const filtered = locations.filter((location) => {
      if (activeChecked === true && archivedChecked === true) {
        // Show all locations
        return location.archive == false;
      } else if (activeChecked === true && archivedChecked === false) {
        return location.archive === false;
      } else if (activeChecked === false && archivedChecked === true) {
        return location.archive === true;
      } else if (activeChecked === false && archivedChecked === false) {
        return location.archive === true;
      }
    });
    setFilteredLocations(filtered);
  };
  // Call filterLocations whenever checkboxes change
  useEffect(() => {
    filterLocations(isActiveChecked, isArchivedChecked);
  }, [isActiveChecked, isArchivedChecked, locations]);
  const handleActiveChange = () => {
    setIsActiveChecked((prev) => !prev);
  };
  const handleArchivedChange = () => {
    setIsArchivedChecked((prev) => !prev);
  };

  const showMap = (id) => {
    setShowMapDialogLocation(true);
    const data = locations.filter((location) => location._id === id);
    setLocationData(data[0]);
  };

  const showRestoreConfirmation = (locationID) => {
    setPendingLocationID(locationID);
    setShowRestoreModal(true);
  };

  const restoreLocation = async () => {
    if (!pendingLocationID) return;

    try {
      setIsLoading(true);
      const res = await api.post(`/location/restore`, {
        id: pendingLocationID,
      });
      setIsLoading(false);
      if (res.data.success) {
        toast.success(res.data.message);
        getLocations();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error("Failed to restore location");
      console.error(error);
      setIsLoading(false);
    } finally {
      setShowRestoreModal(false);
      setPendingLocationID(null);
    }
  };

  return (
    <div className="px-6 mt-20 mb-20 mx-20">
      {isLoading && <Spinner />}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-600">Locations</h1>
        <button
          onClick={() => navigate("/locations/add")}
          className="bg-gray-200 text-gray-600 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-300 transition"
        >
          + Add Location
        </button>
      </div>

      <div className="pt-2">
        <div className="min-w-full !min-h-[55px] flex justify-start items-start pl-2">
          <div className="w-full flex justify-between items-center space-x-2 font-[sans-serif] mt-2">
            <div className="flex w-[50%] items-center gap-2">
              <div
                className={`w-1/2 text-gray-500 bg-[transparent] text-sm tracking-wide flex items-center rounded cursor-pointer hover:!border-gray-200 bg-[transparent]`}
              >
                <div className="w-full relative">
                  <input
                    type="text"
                    placeholder="Search"
                    className="!w-full border border-gray-200 rounded-md py-2 px-4 pl-10 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleLocationKeyDown}
                  />
                  <Search
                    className="absolute left-3 top-2.5 text-gray-500"
                    size={16}
                  />
                </div>
              </div>
              <div
                className={`w-[150px] text-gray-500 bg-[transparent] text-sm tracking-wide flex items-center rounded cursor-pointer hover:!border-gray-200 bg-[transparent]`}
              >
                <div className="text-gray-600 ml-2">
                  Showing {filteredLocations.length} locations
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div
                className={`bg-gray-200 text-sm tracking-wide flex items-center rounded cursor-pointer `}
                onClick={() => changeDisplayMode("grid")}
              >
                <div ref={dropdownRef} className="relative w-max mx-auto">
                  <button
                    type="button"
                    id="dropdownToggle"
                    onClick={toggleDropdown}
                    className="flex px-3 py-2 rounded-md text-gray-600 text-sm font-medium cursor-pointer bg-gray-200 hover:bg-gray-300 active:bg-gray-200"
                  >
                    <i className="fa-solid fa-filter"></i>
                  </button>

                  {isOpen && (
                    <ul
                      id="dropdownMenu"
                      className="absolute block bg-white shadow-lg border-gray-100 py-2 z-[1000] min-w-full w-max rounded-md max-h-96 overflow-auto"
                    >
                      <li
                        className="dropdown-item py-1 px-2 hover:bg-gray-50 text-slate-900 text-sm font-medium cursor-pointer"
                        onClick={handleActiveChange}
                      >
                        <div className="flex items-center">
                          <span className="w-6 h-6 flex items-center justify-center text-gray-600 text-lg">
                            <i
                              className={
                                isActiveChecked
                                  ? "fas fa-check-square"
                                  : "far fa-square"
                              }
                            ></i>
                          </span>
                          <p className="text-sm text-gray-600 ml-4">Active</p>
                        </div>
                      </li>
                    </ul>
                  )}
                </div>
              </div>
              <button
                onClick={() => setDisplayMode("grid")}
                type="button"
                className="flex px-3 py-2 rounded-md text-gray-600 text-sm font-medium cursor-pointer bg-gray-200 hover:bg-gray-300 active:bg-gray-200"
              >
                <i className="fa-solid fa-list"></i>
              </button>
              <div
                onClick={() => setDisplayMode("map")}
                className={` bg-gray-200 px-3 py-2 text-sm tracking-wide flex items-center rounded hover:bg-gray-200 cursor-pointer hover:!border-gray-200`}
              >
                <i className="fa-solid fa-map-location-dot"></i>
              </div>
              {/* <button
                type="button"
                className="flex px-3 py-2 rounded-md text-gray-600 text-sm font-medium cursor-pointer bg-gray-200 hover:bg-gray-300 active:bg-gray-200"
              >
                <i className="fa-solid fa-binoculars"></i>
              </button> */}
            </div>
          </div>
        </div>
      </div>

      {displayMode === "grid" && (
        <div className="rounded-md mt-2 overflow-hidden">
          <div className="overflow-y-auto max-h-[73vh] pb-20">
            <table className="w-full border-collapse" style={{ tableLayout: "fixed" }}>
              <thead className="sticky top-0 bg-gray-200 z-10">
                <tr className="text-left text-sm text-gray-700">
                  <th className="py-3 px-4" style={{ width: "25%" }}>Location Name</th>
                  <th className="py-3 px-4" style={{ width: "15%" }}>Company</th>
                  <th className="py-3 px-4" style={{ width: "15%" }}>Zip Code</th>
                  <th className="py-3 px-4" style={{ width: "35%" }}>Area</th>
                  <th className="py-3 px-4 text-center" style={{ width: "10%" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLocations.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-6 text-gray-500">
                      No locations found.
                    </td>
                  </tr>
                ) : (
                  filteredLocations.map((location) => (
                    <tr
                      key={location._id}
                      className="border-t text-sm text-gray-800 hover:bg-gray-50"
                    >
                      <td 
                        className="py-3 px-4 font-bold !text-gray-600 overflow-hidden text-ellipsis whitespace-nowrap" 
                        title={location.name || "-"}
                      >
                        {location.name}
                      </td>
                      <td 
                        className="py-3 px-4 text-gray-600 overflow-hidden text-ellipsis whitespace-nowrap" 
                        title={location.companyID?.name || "-"}
                      >
                        {location.companyID?.name || "-"}
                      </td>
                      <td 
                        className="py-3 px-4 text-gray-700 overflow-hidden text-ellipsis whitespace-nowrap" 
                        title={location.zipCode || "-"}
                      >
                        <div className="flex items-center gap-2">
                          <svg
                            className="w-4 h-4 text-gray-600 hover:text-gray-800 transition duration-200 flex-shrink-0"
                            focusable="false"
                            aria-hidden="true"
                            viewBox="0 0 24 24"
                            fill="gray"
                          >
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 1.74.5 3.37 1.41 4.84.95 1.54 2.2 2.86 3.16 4.4.47.75.81 1.45 1.17 2.26.26.55.47 1.5 1.26 1.5s1-.95 1.25-1.5c.37-.81.7-1.51 1.17-2.26.96-1.53 2.21-2.85 3.16-4.4C18.5 12.37 19 10.74 19 9c0-3.87-3.13-7-7-7m0 9.75c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5" />
                          </svg>
                          <span className="overflow-hidden text-ellipsis whitespace-nowrap">{location.zipCode || "-"}</span>
                        </div>
                      </td>
                      <td 
                        className="py-3 px-4 text-gray-600 overflow-hidden text-ellipsis whitespace-nowrap" 
                        title={location.area || "-"}
                      >
                        {location.area || "-"}
                      </td>
                      <td className="py-3 px-4 text-left flex justify-center gap-3">
                        {location.archive ? (
                          <button
                            onClick={() => showRestoreConfirmation(location._id)}
                            className="px-3 py-1.5 bg-gray-400 text-white text-sm font-medium rounded-md hover:bg-gray-300 transition"
                            title="Restore Location"
                          >
                            <i className="fa fa-undo mr-1"></i>
                            Restore
                          </button>
                        ) : (
                          <SettingButton
                            locations={locations.map((item) => ({
                              name: item.name,
                              id: item._id,
                            }))}
                            zipCode={location.zipCode}
                            getLocations={getLocations}
                            locationName={location.name}
                            id={location._id}
                          />
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {displayMode === "map" && (
        <IrelandMap zipcodes={locations.map((item) => item.zipCode)} />
      )}

      {/* Modify Dialog */}
      <ModifyDialog
        location={modifyDialogLocation}
        onClose={() => setModifyDialogLocation(null)}
        onSave={updateLocation}
      />

      {/* Delete Dialog */}
      <DeleteDialog
        location={deleteDialogLocation}
        onClose={() => setDeleteDialogLocation(null)}
        onConfirm={handleHideLocation}
      />

      {showMapDialogLocation && (
        <div id="modal">
          <div className="fixed inset-0 p-4 flex flex-wrap justify-center items-center w-full h-full z-[1000] before:fixed before:inset-0 before:w-full before:h-full before:bg-[rgba(0,0,0,0.5)] overflow-auto">
            <div className="w-[900px] bg-white shadow-lg rounded-lg p-6 relative">
              <div className="flex items-center pb-3 border-b border-gray-300">
                <h3 className="text-slate-900 text-xl font-semibold flex-1 text-center">
                  {locationData.name || ""}
                </h3>
                <svg
                  id="closeIcon"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-3.5 h-3.5 ml-2 cursor-pointer shrink-0 fill-gray-400 hover:fill-red-500"
                  viewBox="0 0 320.591 320.591"
                  onClick={() => setShowMapDialogLocation(false)}
                >
                  <path
                    d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z"
                    data-original="#000000"
                  />
                  <path
                    d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z"
                    data-original="#000000"
                  />
                </svg>
              </div>
              <div className="my-6"></div>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-700">Restore Location</h2>
              <button
                onClick={() => {
                  setShowRestoreModal(false);
                  setPendingLocationID(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-base text-gray-700">
                Are you sure you want to restore this location? It will be added back to the active locations list.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowRestoreModal(false);
                  setPendingLocationID(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={restoreLocation}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition"
              >
                Restore
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
